import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, AreaChart, Area, CartesianGrid
} from "recharts";
import API_BASE from "../config";

const Reports = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [types, setTypes] = useState(location.state?.type || "All Time");

    // ================= NEW REPORT LOGIC (Financials) =================
    const [modalVisible, setModalVisible] = useState(false);
    const [modalVisible2, setModalVisible2] = useState(false);

    const [timeFilter, setTimeFilter] = useState("All Time");
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [unitSearch, setUnitSearch] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [allBookingData, setAllBookingData] = useState([]); // Used for both new financials and old charts
    const [projectData, setProjectData] = useState([]);
    const [paymentData, setPaymentData] = useState([]); // Confirmed payments for financials
    const [demandData, setDemandData] = useState([]);

    const [bookingData, setBookingData] = useState([]); // 'Done' bookings for financials
    const [filteredBookingData, setFilteredBookingData] = useState([]);

    const [totalSalePrice, setTotalSalePrice] = useState(0);
    const [totalBankAmount, setTotalBankAmount] = useState(0);
    const [totalSelfAmount, setTotalSelfAmount] = useState(0);
    const [totalDemandValue, setTotalDemandValue] = useState(0);
    const [totalPaymentReceived, setTotalPaymentReceived] = useState(0);
    const [confirmedBookingValue, setConfirmedBookingValue] = useState(0);

    const [selfReceived, setSelfReceived] = useState(0);
    const [bankReceived, setBankReceived] = useState(0);
    const [demandOverdue, setDemandOverdue] = useState(0);

    // ================= OLD REPORT LOGIC (Charts & Stats) =================
    const [stats, setStats] = useState({});
    const [allPayments, setAllPayments] = useState([]); // All payments for charts
    const [loading, setLoading] = useState(true);

    // Initial Data Fetch
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let userId = "1";
                const session = JSON.parse(localStorage.getItem("user_session"));
                if (session?.id) userId = session.id;

                await Promise.all([
                    getAllBookingDetails(),
                    getBookingDetails(),
                    getProjectData(),
                    getPaymentDetails(), // Confirmed payments
                    getDemandData(),
                    // Old Fetch Calls
                    getStats(userId),
                    getAllPayments() // All payments
                ]);
            } catch (err) {
                console.error("Error fetching initial data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Filter Logic for Financials
    useEffect(() => {
        const filterDataByDate = () => {
            const today = new Date();
            const lastWeek = new Date(today);
            lastWeek.setDate(today.getDate() - 7);
            const lastSixMonths = new Date(today);
            lastSixMonths.setMonth(today.getMonth() - 6);

            const parseDate = (dateString) => {
                if (!dateString) return null;
                const [day, month, year] = dateString.includes('-')
                    ? dateString.split('-')
                    : dateString.split('/');
                let fullYear = year;
                if (year && year.length === 2) fullYear = '20' + year;
                return new Date(fullYear, month - 1, day);
            };

            const filterItem = (item, dateField) => {
                const itemDate = parseDate(item[dateField]);
                const start = fromDate ? new Date(fromDate) : null;
                const end = toDate ? new Date(toDate) : null;

                if (selectedProjectId && item.project_id !== selectedProjectId) return false;

                if (timeFilter === 'All Time') {
                    if (start && end) {
                        if (!itemDate) return false;
                        return itemDate >= start && itemDate <= end;
                    }
                    return true;
                } else if (timeFilter === 'Last Week') {
                    if (!itemDate) return false;
                    return itemDate >= lastWeek && itemDate <= today;
                } else if (timeFilter === 'Last 6 Months') {
                    if (!itemDate) return false;
                    return itemDate >= lastSixMonths && itemDate <= today;
                }
                return true;
            };

            const filteredAllBookings = allBookingData.filter(item => filterItem(item, 'date'));
            const filteredBookings = bookingData.filter(item => filterItem(item, 'date'));
            const filteredDemands = demandData.filter(item => filterItem(item, 'demand_date'));
            const filteredPayments = paymentData.filter(item => filterItem(item, 'payment_collect'));

            // Calculate Financial Totals
            setTotalSalePrice(filteredBookings.reduce((acc, curr) => acc + parseFloat(curr.total_saleprice || 0), 0));
            setTotalBankAmount(filteredBookings.reduce((acc, curr) => acc + parseFloat(curr.loan_amount || 0), 0));
            setTotalSelfAmount(filteredBookings.reduce((acc, curr) => acc + parseFloat(curr.self_pay || 0), 0));
            setTotalDemandValue(filteredDemands.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0));
            setTotalPaymentReceived(filteredPayments.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0));
            setConfirmedBookingValue(filteredAllBookings.reduce((acc, curr) => acc + parseFloat(curr.total_saleprice || 0), 0));

            setSelfReceived(filteredPayments.filter(item => item.payment_part === 'Self').reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0));
            setBankReceived(filteredPayments.filter(item => item.payment_part === 'Bank Loan').reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0));
            setDemandOverdue(filteredDemands.filter(item => item.status === 'Overdue').reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0));
        };

        filterDataByDate();

    }, [allBookingData, bookingData, demandData, paymentData, selectedProjectId, timeFilter, fromDate, toDate]);

    // Search Logic for Units
    useEffect(() => {
        if (unitSearch) {
            setFilteredBookingData(bookingData.filter(item => item.unit && item.unit.toLowerCase().includes(unitSearch.toLowerCase())));
        } else {
            setFilteredBookingData(bookingData);
        }
    }, [bookingData, unitSearch]);


    // --- API Calls ---

    // New APIs
    const getAllBookingDetails = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/allrows`, {
                method: 'POST',
                headers: { 'Authorization': '1', 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: '3232', table: 'bookings' })
            });
            const json = await res.json();
            if (Array.isArray(json.data)) setAllBookingData(json.data);
        } catch (e) { console.error(e); }
    };

    const getBookingDetails = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/analyser`, {
                method: 'POST',
                headers: { 'Authorization': '1', 'Content-Type': 'application/json' },
                body: JSON.stringify({ table: 'bookings', key: 'r_status', id: 'Done' })
            });
            const json = await res.json();
            if (Array.isArray(json.data)) setBookingData(json.data);
        } catch (e) { console.error(e); }
    };

    const getProjectData = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/analyser`, {
                method: 'POST',
                headers: { 'Authorization': '1', 'Content-Type': 'application/json' },
                body: JSON.stringify({ table: 'projects', key: 'active', id: '1' })
            });
            const json = await res.json();
            if (Array.isArray(json.data)) setProjectData(json.data);
        } catch (e) { console.error(e); }
    };

    const getPaymentDetails = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/analyser`, {
                method: 'POST',
                headers: { 'Authorization': '1', 'Content-Type': 'application/json' },
                body: JSON.stringify({ table: 'payments', key: 'status', id: 'Confirmed' })
            });
            const json = await res.json();
            if (Array.isArray(json.data)) setPaymentData(json.data);
        } catch (e) { console.error(e); }
    };

    const getDemandData = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/analyser`, {
                method: 'POST',
                headers: { 'Authorization': '1', 'Content-Type': 'application/json' },
                body: JSON.stringify({ table: 'demands', key: 'active', id: '1' })
            });
            const json = await res.json();
            if (Array.isArray(json.data)) setDemandData(json.data);
        } catch (e) { console.error(e); }
    };

    // Old APIs
    const getStats = async (userId) => {
        try {
            const statsRes = await fetch(`${API_BASE}/api/getoverduetask`, {
                method: "POST",
                headers: { Authorization: "1", "Content-Type": "application/json" },
                body: JSON.stringify({ table: "task", user_id: userId }),
            });
            const statsData = await statsRes.json();
            if (statsData.status) setStats(statsData.data);
        } catch (e) { console.error(e); }
    };

    const getAllPayments = async () => {
        try {
            const paysRes = await fetch(`${API_BASE}/api/allrows`, {
                method: "POST",
                headers: { Authorization: "1", "Content-Type": "application/json" },
                body: JSON.stringify({ token: "3232", table: "payments" }),
            });
            const paysData = await paysRes.json();
            if (Array.isArray(paysData.data)) setAllPayments(paysData.data);
        } catch (e) { console.error(e); }
    };


    // --- Helpers & Chart Data Prep ---
    const formatAmount = (amt) => {
        return parseFloat(amt || 0).toLocaleString('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    };

    const handleDateChange = (e, type) => {
        if (type === 'from') setFromDate(e.target.value);
        if (type === 'to') setToDate(e.target.value);
    };

    const remainBalance = totalSalePrice - totalPaymentReceived;
    const remainSelf = totalSelfAmount - selfReceived;
    const remainBank = totalBankAmount - bankReceived;

    // Chart Data Preparation using allBookingData and allPayments
    const monthlySales = allBookingData.reduce((acc, curr) => {
        const d = new Date(curr.date);
        if (isNaN(d.getTime())) return acc;
        const key = d.toLocaleString('default', { month: 'short', year: '2-digit' });
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});
    const salesData = Object.entries(monthlySales).map(([name, value]) => ({ name, Sales: value })).slice(-6);

    const projectDist = allBookingData.reduce((acc, curr) => {
        const p = curr.project_name || "Unknown";
        acc[p] = (acc[p] || 0) + 1;
        return acc;
    }, {});
    const projectDistData = Object.entries(projectDist).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 5);

    const monthlyRevenue = allPayments.reduce((acc, curr) => {
        const d = new Date(curr.date);
        if (isNaN(d.getTime())) return acc;
        const key = d.toLocaleString('default', { month: 'short', year: '2-digit' });
        acc[key] = (acc[key] || 0) + Number(curr.amount || 0);
        return acc;
    }, {});
    const revenueData = Object.entries(monthlyRevenue).map(([name, value]) => ({ name, Revenue: value })).slice(-6);

    const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

    const registryChartData = [
        { name: "Open", value: stats.openregistry || 0, color: "#f59e0b" },
        { name: "Scheduled", value: stats.schregistry || 0, color: "#3b82f6" },
        { name: "Done", value: stats.doneregistry || 0, color: "#10b981" },
    ];

    const loanChartData = [
        { name: "Open", value: stats.openloan || 0, color: "#f59e0b" },
        { name: "Applied", value: stats.appliedloan || 0, color: "#3b82f6" },
        { name: "Sanctioned", value: stats.sanctionloan || 0, color: "#10b981" },
    ];

    const totalUnits = Number(stats.units || 0);
    const allocated = Number(stats.bookin || 0);
    const available = Math.max(0, totalUnits - allocated);
    const unitChartData = [
        { name: "Available", value: available, color: "#cbd5e1" },
        { name: "Sold", value: allocated, color: "#6366f1" },
    ];

    const taskChartData = [
        { name: "Current", value: stats.curentTask || 0, color: "#3b82f6" },
        { name: "Completed", value: stats.completedTask || 0, color: "#10b981" },
        { name: "Overdue", value: stats.overdueTask || 0, color: "#ef4444" },
    ];

    const demandChartData = [
        { name: "Open", value: stats.dopen || 0, color: "#f59e0b" },
        { name: "Overdue", value: stats.doverdue || 0, color: "#ef4444" },
    ];


    return (
        <>
            <style>
                {`
                .rp-container { max-width: 1200px; margin: 0 auto; padding-bottom: 40px; display: flex; flex-direction: column; gap: 30px; }
                
                /* Header */
                .rp-header { display: flex; justify-content: space-between; align-items: center; }
                .rp-title { font-size: 24px; font-weight: 700; color: #0f172a; }
                .rp-desc { font-size: 14px; color: #64748b; margin-top: 4px; }
                .rp-filter-btn {
                    padding: 8px 16px; 
                    background: #fff; border: 1px solid #e2e8f0; 
                    border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    font-size: 14px; color: #334155; font-weight: 500;
                }
                .rp-filter-btn:hover { background: #f8fafc; }

                /* Financial Grid */
                .rp-dashboard-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
                    gap: 16px;
                }
                .rp-card {
                    background: #fff; border-radius: 16px; padding: 20px;
                    border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.04);
                    transition: transform 0.2s; cursor: pointer;
                }
                .rp-card:hover { transform: translateY(-2px); box-shadow: 0 4px 6px rgba(0,0,0,0.05); border-color: #a5b4fc; }
                .rp-card-val { font-size: 24px; font-weight: 700; color: #0f172a; margin-bottom: 4px; white-space: nowrap; }
                .rp-card-label { font-size: 13px; font-weight: 500; color: #64748b; }

                /* Stats Row */
                .rp-stat-row { display:grid; grid-template-columns:repeat(5, 1fr); gap:16px; }
                .rp-stat-box { background:#f8fafc; padding:16px; border-radius:12px; border:1px solid #e2e8f0; text-align:center; }
                .rp-stat-num { font-size:20px; font-weight:700; color:#0f172a; }
                .rp-stat-txt { font-size:12px; color:#64748b; margin-top:4px; font-weight:500; text-transform:uppercase; }

                /* Chart Charts */
                .rp-charts-grid { display:grid; grid-template-columns:repeat(2, 1fr); gap:20px; }
                .rp-chart-card { background:#fff; border-radius:16px; padding:24px; border:1px solid #e2e8f0; height: 360px; display:flex; flex-direction:column; }
                .rp-chart-title { font-size:16px; font-weight:600; color:#0f172a; margin-bottom:20px; }
                .rp-chart-area { flex: 1; width:100%; min-height: 0; }
                
                /* Modal */
                .rp-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
                .rp-modal { background: #fff; width: 90%; max-width: 400px; border-radius: 16px; padding: 24px; max-height: 80vh; overflow-y: auto; }
                .rp-modal-header { font-size: 18px; font-weight: 700; margin-bottom: 16px; }
                .rp-modal-item { padding: 12px; border-bottom: 1px solid #f1f5f9; cursor: pointer; font-size: 14px; }
                .rp-modal-item:hover { background: #f8fafc; }
                .rp-modal-close { margin-top: 16px; width: 100%; padding: 12px; background: #f1f5f9; border: none; border-radius: 8px; color: #334155; font-weight: 600; cursor: pointer; }
                .rp-input-group { margin-bottom: 16px; }
                .rp-input-group label { display: block; font-size: 12px; font-weight: 600; color: #64748b; margin-bottom: 6px; }
                .rp-input { width: 100%; padding: 10px; border-radius: 8px; border: 1px solid #e2e8f0; font-family: inherit; font-size: 14px; }

                @media(max-width:1100px) { .rp-stat-row{grid-template-columns:repeat(3,1fr);} }
                @media(max-width:1024px) { .rp-charts-grid{grid-template-columns:1fr;} }
                @media(max-width:600px) { .rp-stat-row{grid-template-columns:1fr 1fr;} }
                `}
            </style>

            <div className="rp-container">
                {/* 1. Header & Filters */}
                <div className="rp-header">
                    <div>
                        <h1 className="rp-title">Project Analyzer & Reports</h1>
                        <p className="rp-desc">Financial Overview, Sales Performance, and Operational Stats.</p>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button className="rp-filter-btn" onClick={() => setModalVisible(true)}>
                            ⏱️ {timeFilter}
                        </button>
                        <button className="rp-filter-btn" onClick={() => setModalVisible2(true)}>
                            🔍 Filter
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>Loading analytical data...</div>
                ) : (
                    <>
                        {/* 2. Financial Overview (New Cards) */}
                        <h3 style={{ fontSize: 18, color: '#334155', margin: 0 }}>💰 Financials</h3>
                        <div className="rp-dashboard-grid">
                            <div className="rp-card" onClick={() => navigate("/dashboard/reports/detail", { state: { type: "booked_revenue" } })}>
                                <div className="rp-card-val">{formatAmount(confirmedBookingValue)}</div>
                                <div className="rp-card-label">Total Booked Revenue</div>
                            </div>
                            <div className="rp-card" onClick={() => navigate("/dashboard/reports/detail", { state: { type: "confirmed_sale" } })}>
                                <div className="rp-card-val">{formatAmount(totalSalePrice)}</div>
                                <div className="rp-card-label">Confirmed Sale Amt</div>
                            </div>
                            <div className="rp-card" onClick={() => navigate("/dashboard/reports/detail", { state: { type: "total_received" } })}>
                                <div className="rp-card-val">{formatAmount(totalPaymentReceived)}</div>
                                <div className="rp-card-label">Total Received</div>
                            </div>
                            <div className="rp-card" onClick={() => navigate("/dashboard/reports/detail", { state: { type: "total_balance" } })}>
                                <div className="rp-card-val">{formatAmount(remainBalance)}</div>
                                <div className="rp-card-label">Total Balance</div>
                            </div>
                            <div className="rp-card" onClick={() => navigate("/dashboard/reports/detail", { state: { type: "self_received" } })}>
                                <div className="rp-card-val">{formatAmount(selfReceived)}</div>
                                <div className="rp-card-label">Self Received</div>
                            </div>
                            <div className="rp-card" onClick={() => navigate("/dashboard/reports/detail", { state: { type: "self_balance" } })}>
                                <div className="rp-card-val">{formatAmount(remainSelf)}</div>
                                <div className="rp-card-label">Self Balance</div>
                            </div>
                            <div className="rp-card" onClick={() => navigate("/dashboard/reports/detail", { state: { type: "bank_received" } })}>
                                <div className="rp-card-val">{formatAmount(bankReceived)}</div>
                                <div className="rp-card-label">Bank Received</div>
                            </div>
                            <div className="rp-card" onClick={() => navigate("/dashboard/reports/detail", { state: { type: "bank_balance" } })}>
                                <div className="rp-card-val">{formatAmount(remainBank)}</div>
                                <div className="rp-card-label">Bank Balance</div>
                            </div>
                            <div className="rp-card">
                                <div className="rp-card-val">{formatAmount(totalDemandValue)}</div>
                                <div className="rp-card-label">Total Demand Value</div>
                            </div>
                            <div className="rp-card">
                                <div className="rp-card-val" style={{ color: '#ef4444' }}>{formatAmount(demandOverdue)}</div>
                                <div className="rp-card-label">Demand Overdue</div>
                            </div>
                        </div>

                        {/* 3. Operational Stats (Old Stats Row) */}
                        <div style={{ marginTop: 10 }}>
                            <h3 style={{ fontSize: 18, color: '#334155', marginBottom: 16 }}>⚡ Key Metrics</h3>
                            <div className="rp-stat-row">
                                <div className="rp-stat-box"><div className="rp-stat-num">{stats.bookin || 0}</div><div className="rp-stat-txt">Bookings</div></div>
                                <div className="rp-stat-box"><div className="rp-stat-num">{stats.cpayment || 0}</div><div className="rp-stat-txt">Payments</div></div>
                                <div className="rp-stat-box"><div className="rp-stat-num">{stats.appliedloan || 0}</div><div className="rp-stat-txt">Loans Applied</div></div>
                                <div className="rp-stat-box"><div className="rp-stat-num">{stats.doneregistry || 0}</div><div className="rp-stat-txt">Registries</div></div>
                                <div className="rp-stat-box"><div className="rp-stat-num">{stats.dopen || 0}</div><div className="rp-stat-txt">Open Demands</div></div>
                            </div>
                        </div>

                        {/* 4. Detailed Charts (Old Charts) */}
                        <h3 style={{ fontSize: 18, color: '#334155', margin: 0 }}>📈 Analytics & Trends</h3>
                        <div className="rp-charts-grid">
                            {/* Monthly Sales */}
                            <div className="rp-chart-card">
                                <div className="rp-chart-title">Monthly Sales Trend</div>
                                <div className="rp-chart-area">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={salesData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                            <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                            <Bar dataKey="Sales" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={40} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Revenue Trend */}
                            <div className="rp-chart-card">
                                <div className="rp-chart-title">Revenue Overview</div>
                                <div className="rp-chart-area">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={revenueData}>
                                            <defs>
                                                <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                            <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                            <Area type="monotone" dataKey="Revenue" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRev)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Project Distribution */}
                            <div className="rp-chart-card">
                                <div className="rp-chart-title">Top Projects by Sales</div>
                                <div className="rp-chart-area">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={projectDistData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                                {projectDistData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend verticalAlign="middle" align="right" layout="vertical" iconType="circle" />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Registry Status */}
                            <div className="rp-chart-card">
                                <div className="rp-chart-title">Registry Status</div>
                                <div className="rp-chart-area">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={registryChartData} cx="50%" cy="50%" outerRadius={80} dataKey="value">
                                                {registryChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend verticalAlign="bottom" height={36} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Loan Status */}
                            <div className="rp-chart-card">
                                <div className="rp-chart-title">Loan Applications</div>
                                <div className="rp-chart-area">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={loanChartData} cx="50%" cy="50%" innerRadius={40} outerRadius={80} dataKey="value">
                                                {loanChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend verticalAlign="bottom" height={36} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Unit Inventory */}
                            <div className="rp-chart-card">
                                <div className="rp-chart-title">Unit Inventory</div>
                                <div className="rp-chart-area">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart layout="vertical" data={unitChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                            <XAxis type="number" hide />
                                            <YAxis dataKey="name" type="category" width={100} tick={{ fill: '#64748b', fontSize: 12, fontWeight: 500 }} axisLine={false} tickLine={false} />
                                            <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                            <Bar dataKey="value" barSize={30} radius={[0, 4, 4, 0]}>
                                                {unitChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Task Stats */}
                            <div className="rp-chart-card">
                                <div className="rp-chart-title">Task Stats</div>
                                <div className="rp-chart-area">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={taskChartData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                            <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                            <Bar dataKey="value" fill="#8884d8" barSize={40} radius={[4, 4, 0, 0]}>
                                                {taskChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Demands Overview */}
                            <div className="rp-chart-card">
                                <div className="rp-chart-title">Demands Overview</div>
                                <div className="rp-chart-area">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={demandChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                                                {demandChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend verticalAlign="bottom" height={36} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                        </div>
                    </>
                )}

                {/* Modals */}
                {modalVisible && (
                    <div className="rp-modal-overlay" onClick={() => setModalVisible(false)}>
                        <div className="rp-modal" onClick={e => e.stopPropagation()}>
                            <div className="rp-modal-header">Select Time Period</div>
                            {['All Time', 'Last Week', 'Last 6 Months'].map(opt => (
                                <div key={opt} className="rp-modal-item" onClick={() => { setTimeFilter(opt); setModalVisible(false); }}>
                                    {opt}
                                </div>
                            ))}
                            <button className="rp-modal-close" onClick={() => setModalVisible(false)}>Close</button>
                        </div>
                    </div>
                )}
                {modalVisible2 && (
                    <div className="rp-modal-overlay" onClick={() => setModalVisible2(false)}>
                        <div className="rp-modal" onClick={e => e.stopPropagation()}>
                            <div className="rp-modal-header">Filter Options</div>
                            <div className="rp-input-group">
                                <label>Project</label>
                                <select className="rp-input" value={selectedProjectId || ""} onChange={(e) => setSelectedProjectId(e.target.value)}>
                                    <option value="">All Projects</option>
                                    {projectData.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div className="rp-input-group">
                                <label>Search Unit</label>
                                <input className="rp-input" placeholder="Search Unit No..." value={unitSearch} onChange={(e) => setUnitSearch(e.target.value)} />
                                {unitSearch && (
                                    <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e2e8f0', marginTop: 5, borderRadius: 8 }}>
                                        {filteredBookingData.map(b => (
                                            <div key={b.id} style={{ padding: 8, fontSize: 13, borderBottom: '1px solid #f1f5f9' }}>
                                                {b.unit} ({b.customer_name})
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="rp-input-group">
                                <label>From Date</label>
                                <input type="date" className="rp-input" onChange={(e) => handleDateChange(e, 'from')} />
                            </div>
                            <div className="rp-input-group">
                                <label>To Date</label>
                                <input type="date" className="rp-input" onChange={(e) => handleDateChange(e, 'to')} />
                            </div>
                            <button className="rp-modal-close" style={{ background: '#3b82f6', color: '#fff' }} onClick={() => setModalVisible2(false)}>Apply Filters</button>
                            <button className="rp-modal-close" onClick={() => {
                                setSelectedProjectId(null);
                                setUnitSearch("");
                                setFromDate("");
                                setToDate("");
                                setTimeFilter("All Time");
                                setModalVisible2(false);
                            }}>Clear Filters</button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default Reports;
